import { Entity } from "../../Entity";
import CompanyFactory from "../../Factories/CompanyFactory";
import { FactoryInterface } from "../../FactoryInterface";
import Image from "../../../Image/Image";
import { CompanyInterface } from "./CompanyInterface";
import { PersonInterface } from "../Person/PersonInterface";
import { PropertyInterface } from "../Property/PropertyInterface";
import { UnitInterface } from "../Unit/UnitInterface";
import { AddressInterface } from "../../../Interface/AddressInterface";
import { AttachmentInterface } from "../Attachment/AttachmentInterface";
import { SubscriptionInterface } from "../Subscription/SubscriptionInterface";
import AgencyFeeContainer from "../../../Entity/Entities/AgencyFee/AgencyFeeContainer";
import { AgencyFeeInterface } from "../AgencyFee/AgencyFeeInterface";
import SubscriptionTypeContainer from "../../../Entity/Entities/Subscription/SubscriptionType/SubscriptionTypeContainer";
import Translator from "../../../Translation/Translator";
import Store from "../../../Store/Store";
import { BankAccountInterface } from "../BankAccount/BankAccountInterface";

class Company extends Entity implements CompanyInterface {

  constructor(public data) {
    super();
  }

  get nameOfEntity() {
    return Translator.trans("company")
  }

  get nameForUrl() {
    return "company";
  }

  get labelForEntity() {
    return "label-company"
  }

  public getFactory(): FactoryInterface {
    return CompanyFactory;
  }

  get id(): number {
    return this.data.id;
  }

  get amazonFolderName(): string {
    return this.data.amazonFolderName;
  }

  get name(): string {
    if (!this.data.name) {
      return "";
    }

    return this.data.name;
  }

  get shortName(): string {
    return this.data.shortName;
  }

  get displayName(): string {
    return this.data.name;
  }

  get listDisplayName(): string {
    return this.nameOfEntity + ", " + this.displayName;
  }

  get email(): string {
    return this.data.email;
  }

  get agencyId(): number {
    return this.data.agencyId;
  }

  get agency(): CompanyInterface {
    const agency: CompanyInterface =  Store.getConfigurator().getState()?.app?.companies?.find((company: CompanyInterface) => company?.isAgency);
    return agency;
  }

  get avatar(): string {
    return this.data.avatar;
  }

  get personIds(): number[] {
    return this.data.personIds;
  }

  get persons(): PersonInterface[] {
    return this.relations("app.persons", "app.personsInCompanies", this.data.id);
  }

  get propertyIds(): number[] {
    return this.data.propertyIds;
  }

  get updatedAt(): Date {
    return this.data.updatedAt;
  }

  public comparer = (a, b) => {
    if (a > b) {
      return 1;
    }
    if (a < b) {
      return -1;
    }

    return 0;
  }

  public propertyComparer = (a, b) => {
    return this.comparer(a.address.locality, b.address.locality) ||
      this.comparer(a.address.route, b.address.route) ||
        this.comparer(a.address.formattedAddress, b.address.formattedAddress);
  }

  get properties(): PropertyInterface[] {
    return this.newRelations("properties.byId", "app.propertiesInCompanies", this.data.id).sort(this.propertyComparer);
  }

  get units(): UnitInterface[] {
    const units = [];

    this.properties
      .forEach((property: PropertyInterface) => {
        property?.units?.forEach((unit: UnitInterface) => {
          units.push(unit);
        });
    })

    return units;
  }

  get subscription(): SubscriptionInterface {
    return this.subscriptionRelation(SubscriptionTypeContainer.pickIdFromLabel("Companies"), this.data.id);
  }

  get isSubscribed(): boolean {
    return this.subscription !== null;
  }

  get phone(): string {
    return this.data.phone;
  }

  get address(): AddressInterface {
    return this.data.address;
  }

  get attachments(): AttachmentInterface[] {
    return this.data.attachments;
  }

  get mainBankAccountId(): string {
    return this.data.mainBankAccountId;
  }

  get mainBankAccountName(): string {
    return this.data.mainBankAccountName;
  }

  get agencyBankAccountId(): string {
    return this.data.agencyBankAccountId;
  }

  get agencyBankAccountName(): string {
    return this.data.agencyBankAccountName;
  }

  get companyBankId(): string {
    return this.data.companyBankId;
  }

  get removed(): boolean {
    return this.data.removed;
  }

  get defaultAgencyFees(): AgencyFeeInterface[] {
    return this.all("app.agencyFees")
    .filter((agencyFee: AgencyFeeInterface) => {
      return  agencyFee.removed === false &&
             (agencyFee.objectType === AgencyFeeContainer.pickIdFromLabel("Agency") ||
              agencyFee.objectType === AgencyFeeContainer.pickIdFromLabel("Owner")) &&
              agencyFee.objectId === this.data.id;
    });
  }

  get inheritedAgencyFee(): AgencyFeeInterface | null {
    if (this.agency && this.agency.defaultAgencyFees.length > 0) {
      return this.agency.defaultAgencyFees[0];
    }

    return null;
  }

  public renderAvatar(params: any = {}): any {
    return Image.getAvatarAbsolutely(this.data.avatar, "company", params);
  }

  get note(): string {
    return this.data.note;
  }

  get companyType(): any {
    return this.data.companyType;
  }
  
  get isOwner(): boolean {
    return this.properties.length > 0 &&
      this.properties.some(property => property.removed === false);
    // return this.companyType === 'owner'
  }

  get isAgency(): boolean {
    return Store.getConfigurator().getState()?.app?.session?.user?.agencyId === this.data.id;
  }
  
  get bic(): string {
    return this.data.bic;
  }

  get savingsAccount(): number {
    return this.data.savingsAccount;
  }

  get companyRegistry(): string {
    return this.data.companyRegistry;
  }

  get vat(): string {
    return this.data.vat;
  }

  get website(): string {
    return this.data.website;
  }

  get generalContactId(): number {
    return this.data.generalContactId;
  }

  get generalContact(): PersonInterface {
    return [...Store.getConfigurator().getState().app.persons]
            .find(person => person.id === this.generalContactId);
  }

  get contactsForCompanies(): any {
    return this.data.contactsForCompanies;
  }

  get invoiceEmailAddress(): any {
    return this.data.invoiceEmailAddress;
  }
  
  get invoiceEmailSubject(): any {
    return this.data.invoiceEmailSubject;
  }

  get isReadonly(): boolean {
    return this.removed;
  }

  get bankAccounts(): BankAccountInterface[] {
    return this.data.bankAccounts;
  }

  get financialStartDate(): Date {
    return this.data.financialStartDate;
  }
}

export { Company };
